/** @graphql agg **/
import { useCallback, useEffect, useMemo, useState } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import type {
	useTeamDataBulkFetchQuery$data,
	useTeamDataBulkFetchQuery$variables,
} from './__generated__/useTeamDataBulkFetchQuery.graphql.ts';

export type TeamQueryData = useTeamDataBulkFetchQuery$data;
export type TeamQueryVariables = useTeamDataBulkFetchQuery$variables;

export type TeamV2 = NonNullable<NonNullable<TeamQueryData['team']>['teamsV2']>[number];

export type TeamData = TeamQueryData['team'];

const TEAMS_ANALYTICS_SOURCE = 'confluence.permissions-principals.teams';
const TEAM_ARI_PREFIX = 'ari:cloud:identity::team/';

const ensureError = (error: unknown): Error =>
	error instanceof Error ? error : new Error(String(error));

const cleanUpTeamData = (teamData: TeamData | null): TeamData | null => {
	if (!teamData?.teamsV2) {
		return teamData;
	}
	const stripAriPrefix = (id: string) => id.replace(TEAM_ARI_PREFIX, '');
	return {
		...teamData,
		teamsV2: teamData.teamsV2.map((team) => {
			if (!team) {
				return team;
			}
			return {
				...team,
				id: stripAriPrefix(team.id ?? ''),
			};
		}),
	};
};

export interface UseTeamDataResult {
	teamData: TeamQueryData['team'] | null;
	isTeamDataLoading: boolean;
	teamDataError: Error | null;
	refetch: (variables?: TeamQueryVariables) => Promise<TeamData | null>;
}

const UseTeamDataBulkFetchQuery = graphql`
	query useTeamDataBulkFetchQuery($teamIds: [ID!]!, $siteId: String!) {
		team {
			teamsV2(ids: $teamIds, siteId: $siteId) @optIn(to: "Team") {
				type {
					name
					verified
				}
				id
				displayName
				description
				largeAvatarImageUrl
			}
		}
	}
`;

export const useTeamData = (cloudId: string, teamIds: string[] = []): UseTeamDataResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const skip = teamIds.length === 0;

	// Local state for refetch results (useQuery doesn't update data when skip=true)
	const [refetchedData, setRefetchedData] = useState<TeamData | null>(null);
	const [isRefetching, setIsRefetching] = useState(false);
	const [refetchError, setRefetchError] = useState<Error | null>(null);

	const {
		data,
		loading: isQueryLoading,
		error: queryError,
		refetch: queryRefetch,
	} = useQueryInterop<TeamQueryData, TeamQueryVariables>(UseTeamDataBulkFetchQuery, {
		variables: { teamIds, siteId: cloudId },
		skip,
		client: getAGGClient(),
		errorPolicy: 'all',
	});

	const teamData = useMemo(() => {
		if (refetchedData) {
			return refetchedData;
		}
		if (!data?.team) {
			return null;
		}
		return cleanUpTeamData(data.team);
	}, [data, refetchedData]);

	const isTeamDataLoading = isQueryLoading || isRefetching;

	const teamDataError = useMemo(() => {
		const error = queryError || refetchError;
		return error ? ensureError(error) : null;
	}, [queryError, refetchError]);

	useEffect(() => {
		if (teamDataError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: TEAMS_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'fetchTeamData',
					attributes: {
						error: teamDataError,
						siteId: cloudId,
					},
				},
			}).fire();
		}
	}, [teamDataError, cloudId, createAnalyticsEvent]);

	const refetch = useCallback(
		async (variables?: TeamQueryVariables): Promise<TeamData | null> => {
			const refetchVariables = variables ?? { teamIds, siteId: cloudId };

			// Skip if no team IDs
			if (!refetchVariables.teamIds || refetchVariables.teamIds.length === 0) {
				return null;
			}

			setIsRefetching(true);
			setRefetchError(null);

			try {
				const result = await queryRefetch(refetchVariables);
				// The result structure depends on useQueryInterop implementation
				const resultData = (result as { data?: TeamQueryData })?.data?.team ?? null;
				const cleanedData = cleanUpTeamData(resultData);
				setRefetchedData(cleanedData);
				return cleanedData;
			} catch (error) {
				setRefetchError(ensureError(error));
				return null;
			} finally {
				setIsRefetching(false);
			}
		},
		[cloudId, queryRefetch, teamIds],
	);

	return {
		teamData,
		isTeamDataLoading,
		teamDataError,
		refetch,
	};
};
